import type { ApolloError } from '@apollo/client';
import { useLazyQuery, useReactiveVar } from '@apollo/client';
import type React from 'react';
import { useMemo, useState } from 'react';
import BatchDownloadDialog from 'src/components/dialogs/BatchDownloadDialog';
import LoadingDialog from 'src/components/dialogs/LoadingDialog';
import type { ActionItem } from 'src/constants';
import {
  ACTION_RULES,
  CAN_DOWNLOAD_GALLERY_SLUG,
  CAN_DOWNLOAD_SLUG,
  REMOVE_FROM_GALLERY_SLUG,
  USER_PERMISSIONS_TO_GALLERY_MULTI_ACTION_MAP,
} from 'src/constants';
import { buildDownloadAvailabilityQ } from 'src/dynamoQueries/download';
import { gatherActionsFromPerms } from 'src/helpers/permissionsHelper';
import { useAssetDownload } from 'src/hooks/download';
import { useCustomToast } from 'src/hooks/menu';
import { useMyPerms } from 'src/hooks/perms';
import viewStateVar from 'src/store/viewState';
import BasePaginationActions from './BasePaginationActions';

interface AvailabilityItem {
  formatType: string;
  formatLabel?: string;
  available: number;
  total: number;
}

interface AvailabilityMap {
  [code: string]: { available: number; total: number };
}

interface AvailabilityOption {
  value: string;
  label: string;
}

interface GalleryTopActionsProps {
  disabled?: boolean;
  downloadGallery: () => void;
  galleryLoading?: boolean;
  galleryError?: ApolloError | null;
}

interface UserPerms {
  userPerms?: Record<string, boolean>;
}

interface MyPermissionsData {
  myPermissions?: UserPerms;
}

const GalleryTopActions: React.FC<GalleryTopActionsProps> = ({
  disabled,
  downloadGallery,
  galleryLoading,
  galleryError,
}) => {
  const {
    data: myPermsData,
    loading: myPermsLoading,
    error: myPermsError,
  } = useMyPerms() as {
    data?: MyPermissionsData;
    loading: boolean;
    error?: ApolloError;
  };
  const { handleAssetDownload } = useAssetDownload(false);
  const [openBatchSelected, setOpenBatchSelected] = useState<boolean>(false);

  const { toastr } = useCustomToast();
  const viewState = useReactiveVar(viewStateVar);
  const [availabilitySelected, setAvailabilitySelected] = useState<AvailabilityMap | null>(null);
  const [availabilityOptions, setAvailabilityOptions] = useState<AvailabilityOption[] | null>(null);
  const [getAvailability, { loading: availabilityLoading }] = useLazyQuery<{
    downloadAvailability: AvailabilityItem[];
  }>(buildDownloadAvailabilityQ(), {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    onCompleted: (data) => {
      try {
        const list = data?.downloadAvailability || [];
        const map: AvailabilityMap = list.reduce((acc, item) => {
          const code = String(item.formatType);
          acc[code] = { available: item.available, total: item.total };
          return acc;
        }, {} as AvailabilityMap);
        const opts: AvailabilityOption[] = list.map((item) => ({
          value: String(item.formatType),
          label: item.formatLabel || String(item.formatType),
        }));
        setAvailabilitySelected(map);
        setAvailabilityOptions(opts);
      } catch (_e) {
        setAvailabilitySelected({});
        setAvailabilityOptions([]);
      }
    },
    onError: () => {
      setAvailabilitySelected({});
      setAvailabilityOptions([]);
    },
  });

  const selectedAssetsCount = viewState.selectedAssets.length;

  const modifiedActions = useMemo(() => {
    if (myPermsLoading || myPermsError) return [];
    const myPerms = myPermsData?.myPermissions?.userPerms || {};

    let actions = gatherActionsFromPerms(myPerms, USER_PERMISSIONS_TO_GALLERY_MULTI_ACTION_MAP) as ActionItem[];

    // Modify action labels based on selection count
    actions = actions.map((action: ActionItem): ActionItem => {
      if (selectedAssetsCount > 0) {
        return {
          ...action,
          label: `${action.label} (${selectedAssetsCount})`,
        };
      }

      if (selectedAssetsCount === 0 && action.slug === CAN_DOWNLOAD_SLUG) {
        return {
          ...action,
          label: `${action.label} Gallery`,
          slug: CAN_DOWNLOAD_GALLERY_SLUG,
        };
      }

      return action;
    });

    // Apply action rules
    actions = actions.map((action: ActionItem): ActionItem => {
      if (action.rules) {
        if (action.rules.includes(ACTION_RULES.REQUIRES_SELECTED_ASSET)) {
          return { ...action, disabled: selectedAssetsCount === 0 };
        }
      }
      return action;
    });

    return actions;
  }, [myPermsData, myPermsLoading, myPermsError, selectedAssetsCount]);

  if (myPermsLoading) return null;
  if (myPermsError) return <div>Error loading permissions</div>;

  if (galleryError) {
    toastr.error('Error loading gallery');
    return null;
  }

  const handleActionClick = (option: ActionItem): void => {
    switch (option.slug) {
      case CAN_DOWNLOAD_GALLERY_SLUG:
        downloadGallery();
        break;
      case CAN_DOWNLOAD_SLUG:
        if (viewState.selectedAssets?.length) {
          setOpenBatchSelected(true);
          getAvailability({
            variables: { globalIds: viewState.selectedAssets },
          });
        } else {
          setOpenBatchSelected(true);
        }
        break;
      case REMOVE_FROM_GALLERY_SLUG:
        viewStateVar({
          ...viewStateVar(),
          globalAction: REMOVE_FROM_GALLERY_SLUG,
        });
        break;
      default:
        toastr.error(`${option.label} action not implemented yet`);
    }
  };

  return (
    <div>
      <BasePaginationActions actions={modifiedActions} onActionClick={handleActionClick} disabled={disabled} />
      {/* Batch dialog for selected assets */}
      <BatchDownloadDialog
        isOpen={openBatchSelected}
        availability={availabilitySelected}
        options={availabilityOptions}
        loading={availabilityLoading}
        onConfirm={({ formatType }) => {
          if (formatType) {
            handleAssetDownload(viewState.selectedAssets, { formatType, fallbackPolicy: 'SKIP_MISSING' });
          }
          setOpenBatchSelected(false);
        }}
        onCancel={() => setOpenBatchSelected(false)}
      />
      {galleryLoading && <LoadingDialog header="Loading Gallery for download..." />}
    </div>
  );
};

export default GalleryTopActions;
